/**
 * Routes de l'Espace Formateur — sous `/formateur/*`.
 *
 * Garde : utilisateur doit être loggé. Si pas formateur, redirige vers /dashboard.
 * Si pas loggé du tout, redirige vers /login.
 *
 * Injecte aussi le manifest PWA dédié pour permettre l'installation comme app.
 */
import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';

import Accueil from './pages/Accueil';
import Messages from './pages/Messages';
import Supports from './pages/Supports';
import Planning from './pages/Planning';
import MesExamens from './pages/MesExamens';
import DashboardPresences from './pages/DashboardPresences';
import Conduite from './pages/Conduite';
import MessagesParents from './pages/MessagesParents';
import SaisieNotes from './pages/SaisieNotes';
import Profil from './pages/Profil';
import MessagerieInterne from './pages/MessagerieInterne';
import DepotSujets from './pages/DepotSujets';
import Statistiques from './pages/Statistiques';
import MessagerieFloating from '../components/Messagerie/MessagerieFloating';
import { ChatDrawersStack } from '../components/Messagerie/ChatDrawersStack';
import FormateurInstallPrompt from './components/FormateurInstallPrompt';

/** Active la PWA Formateur : manifest dédié + theme-color + Service Worker.
 *  Retire les autres manifest tag pour éviter conflit avec le portail étudiant.
 */
const useManifestFormateur = () => {
  useEffect(() => {
    // Nettoie les manifests existants (ex : celui du portail étudiant)
    document.querySelectorAll('link[rel="manifest"]').forEach((el) => {
      if ((el as HTMLLinkElement).id !== 'formateur-manifest') {
        el.parentNode?.removeChild(el);
      }
    });
    if (!document.getElementById('formateur-manifest')) {
      const link = document.createElement('link');
      link.rel = 'manifest';
      link.href = '/formateur-manifest.json';
      link.id = 'formateur-manifest';
      document.head.appendChild(link);
    }

    // Theme color (couleur barre statut Android)
    let themeMeta = document.querySelector('meta[name="theme-color"]') as HTMLMetaElement | null;
    const previousTheme = themeMeta?.content || '';
    if (!themeMeta) {
      themeMeta = document.createElement('meta');
      themeMeta.name = 'theme-color';
      document.head.appendChild(themeMeta);
    }
    themeMeta.content = '#1e3a8a';

    // Apple touch icon + meta iOS pour l'install
    if (!document.querySelector('link[rel="apple-touch-icon"]')) {
      const apple = document.createElement('link');
      apple.rel = 'apple-touch-icon';
      apple.href = '/logo192.png';
      document.head.appendChild(apple);
    }
    if (!document.querySelector('meta[name="apple-mobile-web-app-capable"]')) {
      const capable = document.createElement('meta');
      capable.name = 'apple-mobile-web-app-capable';
      capable.content = 'yes';
      document.head.appendChild(capable);
    }

    // Service Worker (push + offline)
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => { /* silencieux */ });
    }

    return () => {
      // Restaure le theme-color initial uniquement (le manifest reste, l'utilisateur peut revenir)
      if (themeMeta && previousTheme) themeMeta.content = previousTheme;
    };
  }, []);
};

const FormateurGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useManifestFormateur();
  const user = useSelector((s: RootState) => s.auth.user);
  const token = useSelector((s: RootState) => s.auth.token);

  if (!token) return <Navigate to="/login" replace />;
  const role = user?.role;
  // Seuls les formateurs peuvent utiliser cet espace. Les admins / super_admin
  // qui s'y retrouvent (ex : clic sur une notif push destinée à un formateur
  // alors qu'ils sont connectés en admin) voient un écran explicite plutôt
  // qu'une redirection silencieuse qui casserait la PWA (start_url=/formateur/*
  // → guard rebound → boucle avec le PWAStandaloneGuard).
  if (role === 'admin' || role === 'super_admin') {
    return <WrongRoleScreen role={role} nom={`${user?.prenoms || ''} ${user?.nom || ''}`.trim()} />;
  }
  if (role !== 'formateur') {
    return <Navigate to="/dashboard" replace />;
  }
  return <>{children}</>;
};

/** Écran affiché quand un admin ouvre l'espace formateur par erreur.
 *  Deux issues claires : retourner au dashboard admin OU se déconnecter pour
 *  saisir un compte formateur. */
const WrongRoleScreen: React.FC<{ role: string; nom: string }> = ({ role, nom }) => {
  const doLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    window.location.href = '/login';
  };
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full rounded-2xl border-2 border-amber-500/60 bg-gray-900 shadow-2xl overflow-hidden">
        <div className="bg-amber-500 text-gray-900 px-5 py-3 font-bold text-sm">
          Espace formateur — accès non prévu pour ce compte
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div>
            Vous êtes actuellement connecté(e) en tant que{' '}
            <strong>{nom || 'Utilisateur'}</strong>
            <span className="ml-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 uppercase font-semibold">
              {role}
            </span>.
          </div>
          <div className="text-gray-400 text-[13px] leading-relaxed">
            Cet espace est réservé aux <strong>formateurs</strong> (planning, notes,
            dépôt de sujets, messages parents…). Un compte administrateur n'a pas
            de matières attribuées, votre affichage serait vide et incohérent.
          </div>
          <div className="text-gray-400 text-[12px]">
            Vous êtes probablement arrivé(e) ici depuis un lien ou une notification
            destinée à un formateur, ou depuis la PWA formateur installée sur
            cet appareil.
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <a href="/dashboard"
              className="inline-flex items-center justify-center gap-2 py-2.5 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-semibold">
              Aller au tableau de bord admin
            </a>
            <button onClick={doLogout}
              className="inline-flex items-center justify-center gap-2 py-2.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-gray-100 text-sm font-medium">
              Se déconnecter et ouvrir un compte formateur
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function FormateurRoutes() {
  return (
    <FormateurGuard>
      <Routes>
        <Route index element={<Navigate to="accueil" replace />} />
        <Route path="accueil" element={<Accueil />} />
        {/* Anciennes routes : redirection vers l'accueil (fil intégré) */}
        <Route path="actualites" element={<Navigate to="/formateur/accueil" replace />} />
        <Route path="fil" element={<Navigate to="/formateur/accueil" replace />} />
        <Route path="messages" element={<Messages />} />
        <Route path="messages-parents" element={<MessagesParents />} />
        <Route path="supports" element={<Supports />} />
        <Route path="planning" element={<Planning />} />
        <Route path="mes-examens" element={<MesExamens />} />
        <Route path="presences" element={<DashboardPresences />} />
        <Route path="conduite" element={<Conduite />} />
        <Route path="saisie-notes" element={<SaisieNotes />} />
        <Route path="profil" element={<Profil />} />
        <Route path="messagerie-interne" element={<MessagerieInterne />} />
        <Route path="depot-sujets" element={<DepotSujets />} />
        <Route path="statistiques" element={<Statistiques />} />
        {/* Fix 2026-09-02 : chemin ABSOLU. `to="accueil"` (relatif) crée
            une boucle sur URL /formateur/xxx/… → /formateur/xxx/accueil →
            catch-all → /formateur/xxx/accueil/accueil → boucle. */}
        <Route path="*" element={<Navigate to="/formateur/accueil" replace />} />
      </Routes>
      {/* Bouton flottant messagerie annuaire (heartbeat + contacts + chat direct) */}
      <MessagerieFloating />
      {/* Bandeau d'installation PWA (Android + iOS) — épuré, non intrusif */}
      <FormateurInstallPrompt />
      {/* Fix 2026-09-02 : ChatDrawersStack manquait dans le portail formateur.
          Sans lui, cliquer sur un contact dans le drawer online → dispatch(openChat)
          alimentait bien le store, mais AUCUN composant ne rendait le drawer de
          conversation → rien ne s'affichait. Le stack est présent dans MainLayout
          (backoffice) mais pas ici. Ajout : idem [[feedback_portail_boucle_messagerie_pwa]]
          pour le portail étudiant. */}
      <ChatDrawersStack />
    </FormateurGuard>
  );
}
